import { Box, Flex, FormControl, FormLabel } from "@chakra-ui/react";
import { ReactNode } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { RanaAlert } from "../components/rana-alert";
import { RanaLoading } from "../components/rana-loading";
import { RanaSelect, RanaSelectProps } from "../rana-select";

import { useRanaProjectListQuery } from "./query-projects";

export interface RanaProjectSelectProps
  extends Omit<RanaSelectProps<string | null>, "options" | "value" | "onChange"> {
  value: string | null;
  onChange: (value: string | null) => void;
  label?: ReactNode;
}

export function RanaProjectSelect({ value, onChange, label, ...rest }: RanaProjectSelectProps) {
  const intl = useIntl();
  const projectsQuery = useRanaProjectListQuery();
  const projects = projectsQuery.data?.items ?? [];
  const catalogLabel = intl.formatMessage({
    id: "project.select.rana_catalog",
    defaultMessage: "Rana Catalog (not part of a Rana project)",
  });
  const options = [
    {
      label: (
        <FormattedMessage
          id="project.select.rana_catalog"
          defaultMessage="Rana Catalog (not part of a Rana project)"
        />
      ),
      value: null,
      searchBy: catalogLabel,
    },
    ...projects.map((project) => ({
      label: project.name,
      value: project.id,
      searchBy: project.name,
    })),
  ];

  return (
    <FormControl>
      <FormLabel>
        {label ?? <FormattedMessage id="project.select.label" defaultMessage="Project" />}
      </FormLabel>
      <Flex alignItems="center" gap={2}>
        <Box flex={1}>
          <RanaSelect
            {...rest}
            value={value}
            onChange={onChange}
            options={options}
            isReadOnly={projectsQuery.isLoading || (projectsQuery.isError && !projects.length)}
            w="full"
          />
        </Box>
        {projectsQuery.isLoading && <RanaLoading boxSize={5} />}
      </Flex>
      {projectsQuery.isError && (
        <RanaAlert status="error" mt={2}>
          <FormattedMessage
            id="project.select.load_error"
            defaultMessage="Projects could not be loaded. Showing the Rana catalog."
          />
        </RanaAlert>
      )}
    </FormControl>
  );
}
